import { useState, useCallback } from "react";
import { Pencil, ExternalLink, Check, X, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getERPClient } from "@/lib/erp/client";
import type {
  PackedItem,
  SalesOrderItemCustomization,
} from "@/lib/erp/types/order";
import { parseCustomizationLabel } from "@/features/selling/utils/orderBundleData";

interface OrderItemPersonalizationCardProps {
  customizations: SalesOrderItemCustomization[];
  packedItems: PackedItem[];
  salesOrderName?: string;
  isSubmitted?: boolean;
}

function formatAddonCost(amount: number): string {
  if (amount === 0) return "+฿0";
  return `+฿${amount.toFixed(2)}`;
}

function extractFileName(url: string): string {
  const segment = url.split("/").pop() || url;
  return segment.replace(/^[a-f0-9]{8,}_/, "");
}

interface FieldGroup {
  childItemCode: string | null;
  itemName: string | null;
  fields: Array<{
    cleanLabel: string;
    customization: SalesOrderItemCustomization;
  }>;
}

function groupCustomizations(
  customizations: SalesOrderItemCustomization[],
  packedItems: PackedItem[]
): FieldGroup[] {
  const nameMap = new Map<string, string>();
  for (const pi of packedItems) {
    nameMap.set(pi.item_code, pi.item_name || pi.item_code);
  }

  const buckets = new Map<string, FieldGroup>();
  const nullGroup: FieldGroup = {
    childItemCode: null,
    itemName: null,
    fields: [],
  };

  for (const cust of customizations) {
    const { childItemCode, cleanLabel } = parseCustomizationLabel(
      cust.field_label
    );
    const key = childItemCode ?? "__null__";

    if (childItemCode === null) {
      nullGroup.fields.push({ cleanLabel, customization: cust });
    } else {
      let group = buckets.get(key);
      if (!group) {
        group = {
          childItemCode,
          itemName: nameMap.get(childItemCode) || childItemCode,
          fields: [],
        };
        buckets.set(key, group);
      }
      group.fields.push({ cleanLabel, customization: cust });
    }
  }

  const ordered: FieldGroup[] = [];
  for (const pi of packedItems) {
    const group = buckets.get(pi.item_code);
    if (group) {
      ordered.push(group);
      buckets.delete(pi.item_code);
    }
  }
  for (const group of buckets.values()) {
    ordered.push(group);
  }
  if (nullGroup.fields.length > 0) {
    ordered.push(nullGroup);
  }

  return ordered;
}

const inputClassName =
  "w-full text-xs px-1.5 py-0.5 rounded border border-amber-300 dark:border-amber-700 bg-white dark:bg-amber-950/40 focus:outline-none focus:ring-1 focus:ring-amber-400";

function renderEditControl(
  cust: SalesOrderItemCustomization,
  value: string,
  onChange: (v: string) => void,
  disabled: boolean,
  options?: string[]
) {
  const ft = cust.field_type;

  // Toggle → checkbox
  if (ft === "Toggle") {
    return (
      <label className="inline-flex items-center gap-1.5 cursor-pointer">
        <input
          type="checkbox"
          checked={value === "1"}
          onChange={(e) => onChange(e.target.checked ? "1" : "0")}
          disabled={disabled}
          className="accent-amber-500 h-3.5 w-3.5"
        />
        <span className="text-xs text-foreground/70">
          {value === "1" ? "Yes" : "No"}
        </span>
      </label>
    );
  }

  // Single Select / Dropdown with known options → <select>
  if (
    (ft === "Single Select" || ft === "Dropdown") &&
    options &&
    options.length > 0
  ) {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={inputClassName}
      >
        {!options.includes(value) && value && (
          <option value={value}>{value}</option>
        )}
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }

  // Default: text input
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputClassName}
      disabled={disabled}
    />
  );
}

export function OrderItemPersonalizationCard({
  customizations,
  packedItems,
  salesOrderName,
  isSubmitted,
}: OrderItemPersonalizationCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [fieldOptions, setFieldOptions] = useState<Record<string, string[]>>(
    {}
  );
  const queryClient = useQueryClient();

  const canEdit = !!salesOrderName && !!isSubmitted;
  const hasEditableFields = customizations.some(
    (c) => c.field_type !== "File Upload" && !!c.name
  );

  const handleStartEdit = useCallback(async () => {
    const values: Record<string, string> = {};
    for (const cust of customizations) {
      if (cust.name && cust.field_type !== "File Upload") {
        values[cust.name] = cust.value || "";
      }
    }
    setEditValues(values);
    setIsEditing(true);

    if (salesOrderName) {
      try {
        const res = await getERPClient().call(
          "zaviago_backend.fulfillment.personalization_admin_api.get_customization_field_options",
          { sales_order: salesOrderName }
        );
        if (res && typeof res === "object") {
          setFieldOptions(res as Record<string, string[]>);
        }
      } catch {
        // Options fetch failed — text inputs will be used as fallback
      }
    }
  }, [customizations, salesOrderName]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditValues({});
    setFieldOptions({});
  }, []);

  const handleSave = useCallback(async () => {
    if (!salesOrderName) return;

    const updates = Object.entries(editValues)
      .filter(([rowName, value]) => {
        const original = customizations.find((c) => c.name === rowName);
        return original && value !== (original.value || "");
      })
      .map(([rowName, value]) => ({ row_name: rowName, value }));

    if (updates.length === 0) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await getERPClient().call(
        "zaviago_backend.fulfillment.personalization_admin_api.update_order_customization",
        {
          sales_order: salesOrderName,
          updates: JSON.stringify(updates),
        }
      );
      toast.success("Personalization updated");
      setIsEditing(false);
      setEditValues({});
      setFieldOptions({});
      // Invalidate both query keys that may hold this SO's data:
      // - ["sales-order", name] — used by SalesOrderDetailPage (full page)
      // - ["get", "Sales Order", name] — used by GenericDetailContent (detail panel)
      queryClient.invalidateQueries({ queryKey: ["sales-order", salesOrderName] });
      queryClient.invalidateQueries({ queryKey: ["get", "Sales Order", salesOrderName] });
    } catch (err: any) {
      const msg = err?.message || err?._server_messages || "Failed to update";
      let parsed = msg;
      try {
        parsed = JSON.parse(JSON.parse(msg))?.[0]?.message || msg;
      } catch {
        // not double-encoded, use as-is
      }
      toast.error(parsed);
    } finally {
      setIsSaving(false);
    }
  }, [salesOrderName, editValues, customizations, queryClient]);

  if (customizations.length === 0) return null;

  const isBundle = packedItems.length > 0;
  const groups = groupCustomizations(customizations, packedItems);

  return (
    <div
      role="region"
      aria-label="Personalization details"
      className="border border-amber-200/60 bg-amber-50/50 dark:border-amber-900/30 dark:bg-amber-950/20 rounded-xl px-4 py-3 mt-3 animate-fade-in motion-reduce:animate-none"
    >
      {/* Header */}
      <div className="flex items-center gap-1.5">
        <Pencil className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
        <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
          Personalization
        </span>
        <div className="ml-auto flex items-center gap-1">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="p-1 rounded hover:bg-amber-200/50 dark:hover:bg-amber-800/30 text-foreground/60"
                aria-label="Cancel editing"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="p-1 rounded hover:bg-green-200/50 dark:hover:bg-green-800/30 text-green-700 dark:text-green-400"
                aria-label="Save changes"
              >
                {isSaving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
              </button>
            </>
          ) : (
            canEdit && hasEditableFields && (
              <button
                onClick={handleStartEdit}
                className="p-1 rounded hover:bg-amber-200/50 dark:hover:bg-amber-800/30 text-foreground/50 hover:text-foreground/80"
                aria-label="Edit personalization"
                title="Edit personalization"
              >
                <Pencil className="h-3 w-3" />
              </button>
            )
          )}
        </div>
      </div>

      {/* Separator */}
      <div className="border-t border-amber-200/40 dark:border-amber-800/30 mt-2 mb-2" />

      {/* Field groups */}
      <div className="space-y-3">
        {groups.map((group, gi) => (
          <div key={group.childItemCode ?? `group-${gi}`}>
            {isBundle && group.childItemCode && (
              <div className="text-xs font-medium text-foreground/80 mt-2 mb-1">
                {group.itemName} ({group.childItemCode})
              </div>
            )}

            <dl className="space-y-1">
              {group.fields.map((field, fi) => {
                const cust = field.customization;
                const addonAmount = cust.addon_amount ?? 0;
                const isFile =
                  cust.field_type === "File Upload" && cust.file_url;
                const isEditableField =
                  isEditing && !isFile && !!cust.name;

                return (
                  <div
                    key={`${group.childItemCode ?? "f"}-${fi}`}
                    className="flex items-center gap-1.5 min-h-[24px]"
                  >
                    <dt
                      className="text-xs font-medium text-foreground/70 truncate max-w-[200px]"
                      title={field.cleanLabel}
                    >
                      {field.cleanLabel}:
                    </dt>
                    <dd className="text-xs text-foreground flex-1 min-w-0">
                      {isEditableField ? (
                        renderEditControl(
                          cust,
                          editValues[cust.name!] ?? "",
                          (v) =>
                            setEditValues((prev) => ({
                              ...prev,
                              [cust.name!]: v,
                            })),
                          isSaving,
                          fieldOptions[cust.name!]
                        )
                      ) : isFile ? (
                        <a
                          href={cust.file_url!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-sky-600 dark:text-sky-400 hover:underline truncate inline-flex items-center gap-0.5"
                          aria-label={`View file ${extractFileName(cust.file_url!)}`}
                        >
                          {extractFileName(cust.file_url!)}
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>
                      ) : (
                        <span>{cust.value}</span>
                      )}
                    </dd>
                    {addonAmount > 0 && (
                      <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 ml-auto flex-shrink-0">
                        {formatAddonCost(addonAmount)}
                      </span>
                    )}
                  </div>
                );
              })}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}
